import { AbstractMessageHandler } from './AbstractMessageHandler.ts'
import { ButtonMessageType } from '../enums'
import { isButtonMessageType, isValidSettings } from '../type-guards'
import {getParentColorScheme} from '../parentColorScheme'
import type {Config, State} from '../types'

export class ButtonMessageHandler extends AbstractMessageHandler {
  private readonly onSettingsLoaded: (settings: Record<string, unknown>) => void
  private readonly startButtonSideDrag: (payload?: Record<string, unknown>) => void
  private readonly moveButtonSideDrag: (payload?: Record<string, unknown>) => void
  private readonly endButtonSideDrag: (payload?: Record<string, unknown>) => void

  constructor(
    config: Config,
    state: State,
    sendMessageToButton: (type: string, payload: Record<string, unknown>) => void,
    sendMessageToChat: (type: string, payload: Record<string, unknown>) => void,
    toggleChat: () => void,
    dismissGreeting: () => void,
    scheduleGreeting: () => void,
    logError: (message: string, ...args: unknown[]) => void,
    showError: (message: string) => void,
    onSettingsLoaded: (settings: Record<string, unknown>) => void,
    startButtonSideDrag: (payload?: Record<string, unknown>) => void = () => {},
    moveButtonSideDrag: (payload?: Record<string, unknown>) => void = () => {},
    endButtonSideDrag: (payload?: Record<string, unknown>) => void = () => {}
  ) {
    super(config, state, sendMessageToButton, sendMessageToChat, toggleChat, dismissGreeting, scheduleGreeting, logError, showError)
    this.onSettingsLoaded = onSettingsLoaded
    this.startButtonSideDrag = startButtonSideDrag
    this.moveButtonSideDrag = moveButtonSideDrag
    this.endButtonSideDrag = endButtonSideDrag
  }

  handle(_event: MessageEvent, type: string, payload?: Record<string, unknown>): void {
    try {
      if (!isButtonMessageType(type)) {
        this.logError('Unknown button message type:', type)
        return
      }

      switch (type) {
        case ButtonMessageType.BUTTON_READY:
          this.handleButtonReady()
          break
        case ButtonMessageType.SETTINGS_LOADED:
          this.handleSettingsLoaded(payload)
          break
        case ButtonMessageType.TOGGLE_CHAT:
          this.toggleChat()
          break
        case ButtonMessageType.BUTTON_DRAG_START:
          this.startButtonSideDrag(payload)
          break
        case ButtonMessageType.BUTTON_DRAG_MOVE:
          this.moveButtonSideDrag(payload)
          break
        case ButtonMessageType.BUTTON_DRAG_END:
          this.endButtonSideDrag(payload)
          break
      }
    } catch (error) {
      this.logError('Error in ButtonMessageHandler:', error)
    }
  }

  private handleButtonReady(): void {
    try {
      if (this.state.buttonIframe) {
        this.state.buttonIframe.classList.add('ready')
      }

      if (this.state.widgetContainer) {
        this.state.widgetContainer.classList.add('ready')
      }

      this.sendMessageToButton('init', {
        apiUrl: this.config.apiUrl,
        apiKey: this.config.apiKey,
        parentColorScheme: getParentColorScheme()
      })

      this.scheduleGreeting()
    } catch (error) {
      this.logError('Error handling buttonReady:', error)
    }
  }

  private handleSettingsLoaded(payload?: Record<string, unknown>): void {
    try {
      if (!isValidSettings(payload)) {
        return
      }

      this.onSettingsLoaded(payload.settings)

      const buttonIframe = this.state.buttonIframe
      if (buttonIframe) {
        requestAnimationFrame(() => buttonIframe.style.setProperty('opacity', '1'))
        buttonIframe.style.setProperty(
          'pointer-events',
          'auto'
        )
      }
    } catch (error) {
      this.logError('Error handling settingsLoaded:', error)
    }
  }

}
